import { Link } from 'react-router-dom';
import { propFirmLogos } from '../data/propFirmLogos';
import { StarRating } from './StarRating';
import { PromoCTAButton } from './PromoCTAButton';
import { getBestDiscount } from '../utils/getBestDiscount';
import type { PropFirm } from '../types';

// Helper function to format numbers with commas
const formatNumber = (value: string | number): string => {
  if (typeof value === 'string') {
    // Check if the string contains a number that should be formatted
    if (/^\$?\d+,?\d*\.?\d*[kKmMbB]?$/.test(value)) {
      // Extract the numeric part if it has a currency symbol or suffix
      const numericMatch = value.match(/\d+,?\d*\.?\d*/)?.[0];
      if (numericMatch) {
        // Remove any existing commas
        const withoutCommas = numericMatch.replace(/,/g, '');
        // Format with commas
        const formatted = Number(withoutCommas).toLocaleString();
        // Replace the numeric part in the original string
        return value.replace(numericMatch, formatted);
      }
    }
    return value;
  }
  // If it's a number, simply format it
  return Number(value).toLocaleString();
};

interface ReviewCardProps {
  firm: PropFirm;
}

export function ReviewCard({ firm }: ReviewCardProps) {
  const logo = propFirmLogos[firm.id]?.logo;
  const challenge = firm.pricing?.find((p) => p.phases === 2 && p.accountSize === 100000);
  const discount = getBestDiscount(firm.discounts || [], challenge);

  return (
    <div className="bg-white/10 backdrop-blur rounded-xl shadow-md p-6 flex flex-col h-full hover:scale-[1.01] transition-transform">
      {/* Content area with flex-grow to push buttons to bottom */}
      <div className="flex-grow">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 flex items-center justify-center bg-black rounded">
            <img src={logo} alt={firm.name} className="w-8 h-8 object-contain" />
          </div>
          <div>
            <Link to={`/reviews/${firm.id}`} className="font-bold text-lg text-yellow-100 hover:text-yellow-300">
              {firm.name}
            </Link>
            <div className="flex items-center gap-2 text-sm text-yellow-300">
              <StarRating rating={firm.rating} />
              ({firm.reviewCount.toLocaleString()})
            </div>
          </div>
        </div>

        {/* Fixed height description with overflow handling */}
        <div className="h-[72px] mb-4">
          <p className="text-sm text-yellow-200 line-clamp-3">{firm.description}</p>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div>
            <span className="text-yellow-400 block mb-1">Max Funding</span>
            <span className="font-semibold text-white">${formatNumber(firm.features.maxFunding)}</span>
          </div>
          <div>
            <span className="text-yellow-400 block mb-1">Profit Split</span>
            <span className="font-semibold text-white">{formatNumber(firm.features.profitSplit)}%</span>
          </div>
        </div>
      </div>

      {/* Button container with fixed position at bottom */}
      <div className="flex flex-col gap-2 mt-auto">
        {/* Read Review button - always at the same height */}
        <Link
          to={`/reviews/${firm.id}`}
          className="w-full h-11 inline-flex items-center justify-center gap-2 font-semibold bg-white/10 hover:bg-white/20 text-yellow-400 rounded-lg transition"
        >
          📖 Read Review
        </Link>
        
        {/* Promo button container with consistent height */}
        <div className="h-[60px]">
          <PromoCTAButton
            className="items-center"
            firmId={firm.id}
            code={discount?.code}
            percent={discount?.percent}
            fee={challenge?.fee}
            validUntil={discount?.validUntil}
            firstPurchaseOnly={discount?.firstPurchaseOnly}
          />
        </div>
      </div>
    </div>
  );
}

export default ReviewCard;
